<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { resolveImageAlt } from "@/utils/imageAlt";

const props = defineProps<{
  worldwide?: string;
}>();

const page = usePage().value;

const articleAlt = (article: { title?: string; blogImage?: { alt?: string }; blogImage1?: { alt?: string } }, which: "blogImage" | "blogImage1" = "blogImage") =>
  resolveImageAlt(article?.[which]?.alt, article?.title);

const parseDate = (dateString: string) => {
  const [day, month, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const isWorldwideFilter = computed(() => {
  return (
    props.worldwide &&
    props.worldwide !== "false" &&
    (props.worldwide.toLowerCase() === "worldwide" || props.worldwide === "Worldwide")
  );
});

const articles = computed(() => {
  let allArticles = page?.articles ?? [];

  // Filter articles based on worldwide setting
  if (isWorldwideFilter.value) {
    // Show only worldwide articles
    allArticles = allArticles.filter((article) => {
      const tagString = article?.tags?.value || "";
      const tagsArray = tagString.split(",").map((tag) => tag.trim().toLowerCase());

      return tagsArray.includes("worldwide") || article?.worldwide === true;
    });
  } else if (props.worldwide === "false") {
    // Exclude worldwide articles when explicitly set to false
    allArticles = allArticles.filter((article) => {
      const tagString = article?.tags?.value || "";
      const tagsArray = tagString.split(",").map((tag) => tag.trim().toLowerCase());

      return !tagsArray.includes("worldwide") && article?.worldwide !== true;
    });
  }

  const sortedArticles = allArticles
    .slice()
    .sort((a, b) => parseDate(b.published).getTime() - parseDate(a.published).getTime());

  // Limit to 6 articles for homepage (when worldwide is false)
  if (props.worldwide === "false") {
    return sortedArticles.slice(0, 6);
  }

  return sortedArticles;
});
const itemsToShow = ref(4);
const windowWidth = ref(0); // default value
const isMobile = ref(false);

function updateWidth() {
  if (process.client) {
    windowWidth.value = window.innerWidth;

    if (isMobile.value) {
      itemsToShow.value = 1;
    } else {
      itemsToShow.value = 3;
    }
  }
}

onMounted(() => {
  isMobile.value = window.innerWidth <= 991;
  // Access the window object only after the component is mounted
  windowWidth.value = window.innerWidth;
  updateWidth();
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});
</script>

<template>
  <header v-if="isWorldwideFilter" class="articles-header">
    <h2>Read About Our Worldwide Trekking Experience</h2>
    <h3>Trip Reports</h3>
  </header>
  <header v-else class="articles-header">
    <h2>Experience the Mountains with Us</h2>
    <h3>Read our latest articles</h3>
  </header>
  <section class="articles-wrap">
    <div
      v-for="(article, index) in articles.slice(0, 1)"
      :key="article?.id || article?.uri || index"
      class="article"
    >
      <NuxtLink class="btn from-left" :to="`/${article?.uri}`">
        <div class="article-image">
          <ClientOnly>
            <NuxtPicture
              v-if="article?.blogImage1 && !isMobile"
              format="webp"
              loading="lazy"
              :src="article?.blogImage1.url"
              :width="article?.blogImage1.width"
              :height="article?.blogImage1.height"
              :alt="articleAlt(article, 'blogImage1')"
              sizes="sm:100vw md:50vw lg:600px"
            />
            <NuxtPicture
              v-if="article?.blogImage1 && isMobile"
              format="webp"
              loading="lazy"
              :src="article?.blogImage1.mobileUrl"
              :width="article?.blogImage1.width"
              :height="article?.blogImage1.height"
              :alt="articleAlt(article, 'blogImage1')"
              sizes="sm:100vw md:50vw lg:600px"
            />
          </ClientOnly>
        </div>
        <div class="article-meta">
          <h3>{{ article?.title }}</h3>
          <p class="date">
            {{ article?.published }}
          </p>
        </div>
      </NuxtLink>
    </div>
    <div
      v-for="(article, index) in articles.slice(1, 3)"
      :key="article?.id || article?.uri || index"
      class="article"
    >
      <NuxtLink class="btn from-left" :to="`/${article?.uri}`">
        <div class="article-image">
          <ClientOnly>
            <NuxtPicture
              v-if="article?.blogImage && !isMobile"
              format="webp"
              loading="lazy"
              :src="article?.blogImage.url"
              :width="article?.blogImage.width"
              :height="article?.blogImage.height"
              :alt="articleAlt(article, 'blogImage')"
              sizes="sm:100vw md:50vw lg:600px"
            />
            <NuxtPicture
              v-if="article?.blogImage && isMobile"
              format="webp"
              loading="lazy"
              :src="article?.blogImage.mobileUrl"
              :width="article?.blogImage.width"
              :height="article?.blogImage.height"
              :alt="articleAlt(article, 'blogImage')"
              sizes="sm:100vw md:50vw lg:600px"
            />
          </ClientOnly>
        </div>
        <div class="article-meta">
          <h3>{{ article?.title }}</h3>
          <p class="date">{{ article.published }}</p>
        </div>
      </NuxtLink>
    </div>
  </section>
  <section class="articles-wrap2">
    <div
      v-for="(article, index) in articles.slice(3)"
      :key="article?.id || article?.uri || index"
      class="article"
    >
      <NuxtLink class="btn from-left" :to="`/${article?.uri}`">
        <div class="article-image">
          <ClientOnly>
            <NuxtPicture
              v-if="article?.blogImage && !isMobile"
              format="webp"
              loading="lazy"
              :src="article?.blogImage.url"
              :width="article?.blogImage.width"
              :height="article?.blogImage.height"
              :alt="articleAlt(article, 'blogImage')"
              sizes="sm:100vw md:50vw lg:600px"
            />
            <NuxtPicture
              v-if="article?.blogImage && isMobile"
              format="webp"
              loading="lazy"
              :src="article?.blogImage.mobileUrl"
              :width="article?.blogImage.width"
              :height="article?.blogImage.height"
              :alt="articleAlt(article, 'blogImage')"
              sizes="sm:100vw md:50vw lg:600px"
            />
          </ClientOnly>
        </div>
        <div class="article-meta">
          <h3>{{ article?.title }}</h3>
          <p class="date">{{ article.published }}</p>
        </div>
      </NuxtLink>
    </div>
  </section>
</template>
